// WhatsApp on the tenant's own line: the conversation panel on a lead (Edit.cshtml "ווצאפ") and
// the link panel in Settings (QR code while linking). Message text is inserted as text, never as
// HTML, so whatever a customer writes cannot inject markup into the page.
import { ensureReauth } from "./reauth";

interface WaMessage {
  id: string;
  dir: "in" | "out";
  source: "customer" | "crm" | "phone" | "auto" | "bot";
  kind: string;
  label: string;
  text: string;
  agent: string | null;
  /** The agent whose own line carried it; null on the main line. */
  line: string | null;
  /** With whom, when not the order's contact: a passenger on their own number. */
  peer: string | null;
  /** The main line that carried it (its name), when not the first one. */
  via: string | null;
  day: string;
  time: string;
}

interface WaThreadReply {
  line: { status: string; open: boolean; phone: string | null };
  /** Where this user's message goes out: their own WhatsApp, or the main line. */
  via: "mine" | "main";
  /** The same, as the panel says it: "נשלח מהווצאפ שלך", "נשלח ממספר אילת"… */
  from: string;
  canSend: boolean;
  canStartBot: boolean;
  awaiting: boolean;
  threadId: string | null;
  bot: { status: "active" | "done" | "stopped" | "expired"; step: number; total: number } | null;
  messages: WaMessage[];
}

/** The questionnaire's state, as the panel header says it. */
const botText = (b: WaThreadReply["bot"]): string => {
  if (!b) return "";
  switch (b.status) {
    case "active": return `שאלון אוטומטי: שאלה ${b.step} מתוך ${b.total}`;
    case "done": return "השאלון הושלם";
    case "stopped": return "השאלון נעצר (נציג בשיחה)";
    default: return "השאלון לא הושלם";
  }
};

interface WaLineStatus {
  status: string;
  linked: boolean;
  phone: string | null;
  name: string | null;
  qr: string | null;
  lastError: string | null;
}

const THREAD_POLL_MS = 10_000;
const LINE_POLL_MS = 2_500;

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = "", text = ""): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
};

const today = (): string => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

/** Who wrote an outgoing message, as shown above its bubble. */
const outWho = (m: WaMessage): string =>
  m.source === "auto" ? "תשובה אוטומטית"
    : m.source === "bot" ? "שאלון אוטומטי"
    : m.source === "phone" ? (m.line ? `נשלח מהטלפון של ${m.line}` : m.via ? `נשלח מהטלפון של מספר ${m.via}` : "נשלח מהטלפון")
    : (m.agent || "נציג") + (m.line ? " · מהווצאפ האישי" : m.via ? ` · ממספר ${m.via}` : "");

function renderThread(log: HTMLElement, messages: WaMessage[]): void {
  const atEnd = log.scrollHeight - log.scrollTop - log.clientHeight < 40;
  log.replaceChildren();
  if (messages.length === 0) {
    log.append(el("p", "self-center text-sm text-gray-400", "אין עדיין הודעות ווצאפ עם הלקוח"));
    return;
  }
  let prevDay = "";
  let prevWho = "";
  for (const m of messages) {
    if (m.day !== prevDay) {
      log.append(el("div", "chat-day", m.day === today() ? "היום" : m.day));
      prevDay = m.day;
      prevWho = "";
    }
    const who = (m.dir === "in" ? `customer:${m.line ?? ""}:${m.via ?? ""}` : outWho(m)) + `|${m.peer ?? ""}`;
    const tight = who === prevWho;
    prevWho = who;

    const row = el("div", `chat-msg${m.dir === "out" ? " is-out" : ""}${tight ? " is-tight" : ""}`);
    const bubble = el("div", "chat-bubble");
    if (m.peer && !tight) bubble.append(el("div", "text-xs font-medium text-brand-600", `שיחה עם ${m.peer}`));
    if (m.dir === "out" && !tight) bubble.append(el("div", "chat-who", outWho(m)));
    // A customer who wrote to an agent's own WhatsApp, not to the main line.
    if (m.dir === "in" && m.line && !tight) bubble.append(el("div", "chat-who", `לווצאפ של ${m.line}`));
    // A customer who wrote to another of the company's numbers (service, Eilat…).
    else if (m.dir === "in" && m.via && !tight) bubble.append(el("div", "chat-who", `למספר ${m.via}`));
    if (m.label) bubble.append(el("div", "text-xs font-medium text-gray-500", `[${m.label}]`));
    if (m.text) bubble.append(el("div", "chat-text", m.text));
    bubble.append(el("span", "chat-time", m.time));
    row.append(bubble);
    log.append(row);
  }
  if (atEnd || log.dataset.loaded !== "1") log.scrollTop = log.scrollHeight;
  log.dataset.loaded = "1";
}

function initWaThread(): void {
  const root = document.querySelector<HTMLElement>("[data-wa-thread]");
  if (!root) return;
  const log = root.querySelector<HTMLElement>("[data-wa-log]")!;
  const form = root.querySelector<HTMLFormElement>("[data-wa-form]")!;
  const input = form.querySelector<HTMLTextAreaElement>("textarea")!;
  const send = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
  const state = root.querySelector<HTMLElement>("[data-wa-state]")!;
  const badge = root.querySelector<HTMLElement>("[data-wa-badge]")!;
  const error = root.querySelector<HTMLElement>("[data-wa-error]")!;
  const dismiss = root.querySelector<HTMLFormElement>("[data-wa-dismiss]")!;
  const startBot = root.querySelector<HTMLButtonElement>("[data-wa-startbot]")!;
  const stopBot = root.querySelector<HTMLButtonElement>("[data-wa-stopbot]")!;
  const token = form.querySelector<HTMLInputElement>("input[name=__RequestVerificationToken]")?.value ?? "";
  let lastSig = "";
  let busy = false;
  let canSend = false;

  const showError = (msg: string): void => {
    error.textContent = msg;
    error.hidden = !msg;
  };

  const sync = (): void => {
    send.disabled = busy || !canSend;
    input.disabled = !canSend;
  };

  async function refresh(): Promise<void> {
    try {
      const res = await fetch(root!.dataset.threadUrl!, { headers: { "X-Requested-With": "XMLHttpRequest" } });
      if (!res.ok) return;
      const data = (await res.json()) as WaThreadReply;
      canSend = data.canSend;
      state.textContent = !data.line.open ? "הקו לא מחובר: אפשר לקרוא, אי אפשר לשלוח"
        : botText(data.bot) || data.from;
      badge.hidden = !data.awaiting;
      dismiss.hidden = !(data.awaiting && data.threadId);
      if (data.threadId) dismiss.querySelector<HTMLInputElement>("input[name=id]")!.value = data.threadId;
      startBot.hidden = !data.canStartBot;
      stopBot.hidden = data.bot?.status !== "active";
      const sig = data.messages.map((m) => m.id).join(",");
      if (sig !== lastSig) {
        renderThread(log, data.messages);
        lastSig = sig;
      }
      sync();
    } catch {
      /* the next poll tries again */
    }
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (!text || busy || !canSend) return;
    busy = true;
    sync();
    showError("");
    try {
      const res = await fetch(root.dataset.sendUrl!, {
        method: "POST",
        headers: { "Content-Type": "application/json", RequestVerificationToken: token, "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ leadId: root.dataset.leadId, text }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) showError(data?.error || `השליחה נכשלה (${res.status})`);
      else {
        input.value = "";
        log.dataset.loaded = "";
        await refresh();
      }
    } catch {
      showError("השליחה נכשלה: אין חיבור לשרת");
    } finally {
      busy = false;
      sync();
      input.focus();
    }
  });

  startBot.addEventListener("click", async () => {
    if (busy || !confirm("לשלוח ללקוח את השאלון? התשובות שלו ייכנסו לשדות הליד. הודעה שלך בשיחה עוצרת אותו.")) return;
    busy = true;
    sync();
    showError("");
    try {
      const res = await fetch(startBot.dataset.url!, {
        method: "POST",
        headers: { "Content-Type": "application/json", RequestVerificationToken: token, "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ leadId: root.dataset.leadId }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) showError(data?.error || `השליחה נכשלה (${res.status})`);
      log.dataset.loaded = "";
      await refresh();
    } catch {
      showError("השליחה נכשלה: אין חיבור לשרת");
    } finally {
      busy = false;
      sync();
    }
  });

  stopBot.addEventListener("click", async () => {
    stopBot.disabled = true;
    try {
      await fetch(stopBot.dataset.url!, {
        method: "POST",
        headers: { "Content-Type": "application/json", RequestVerificationToken: token, "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ leadId: root.dataset.leadId }),
      });
      await refresh();
    } finally {
      stopBot.disabled = false;
    }
  });

  // Enter sends, Shift+Enter is a new line — as in WhatsApp itself.
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      form.requestSubmit();
    }
  });

  void refresh();
  window.setInterval(() => {
    if (!document.hidden) void refresh();
  }, THREAD_POLL_MS);
}

function initWaLine(root: HTMLElement): void {
  const title = root.querySelector<HTMLElement>("[data-wa-title]")!;
  const sub = root.querySelector<HTMLElement>("[data-wa-sub]")!;
  const connect = root.querySelector<HTMLButtonElement>("[data-wa-connect]")!;
  const qrBox = root.querySelector<HTMLElement>("[data-wa-qr]")!;
  const qrImg = qrBox.querySelector<HTMLImageElement>("img")!;
  const disconnect = root.querySelector<HTMLElement>("[data-wa-disconnect]")!;
  const token = root.querySelector<HTMLInputElement>("input[name=__RequestVerificationToken]")?.value ?? "";
  let timer = 0;

  const show = (s: WaLineStatus): void => {
    const phone = s.phone ? `+${s.phone}` : "";
    const texts: Record<string, [string, string]> = {
      open: [`מחובר ${phone}`.trim(), s.name ? `שם בווצאפ: ${s.name}` : "הודעות נכנסות נקלטות למערכת"],
      qr: ["ממתין לסריקה", "סרקו את הקוד מהטלפון של המספר"],
      connecting: ["מתחבר…", s.lastError ?? ""],
      unreachable: ["שער הווצאפ לא זמין", s.lastError ?? "התהליך אינו פועל בשרת"],
      replaced: ["החיבור נפתח במקום אחר", "לחצו על חיבור כדי להחזיר אותו לכאן"],
      loggedOut: ["המספר נותק", s.lastError ?? "צריך לחבר מחדש בסריקת QR"],
      idle: ["לא מחובר", s.lastError ?? "לחצו על חיבור מספר כדי להציג קוד QR"],
    };
    const [t, d] = texts[s.status] ?? texts.idle;
    title.textContent = t;
    title.className = `text-sm font-medium ${s.status === "open" ? "text-success-700" : "text-gray-900"}`;
    sub.textContent = d;
    connect.hidden = !["idle", "loggedOut", "replaced"].includes(s.status);
    disconnect.hidden = !(s.status === "open" || s.linked);
    qrBox.hidden = !(s.status === "qr" && s.qr);
    if (s.status === "qr" && s.qr && qrImg.src !== s.qr) qrImg.src = s.qr;

    // Poll only while something is about to change: a QR on screen, or a connection in progress.
    window.clearTimeout(timer);
    if (s.status === "qr" || s.status === "connecting") timer = window.setTimeout(poll, LINE_POLL_MS);
  };

  async function poll(): Promise<void> {
    try {
      const res = await fetch(root!.dataset.statusUrl!, { headers: { "X-Requested-With": "XMLHttpRequest" } });
      if (res.ok) show((await res.json()) as WaLineStatus);
    } catch {
      timer = window.setTimeout(poll, LINE_POLL_MS * 2);
    }
  }

  connect.addEventListener("click", async () => {
    // Linking a line is sensitive (Domain/Reauthentication.cs): the proof first, as on the forms.
    if (!(await ensureReauth())) return;
    connect.disabled = true;
    try {
      const res = await fetch(root.dataset.connectUrl!, {
        method: "POST",
        headers: { RequestVerificationToken: token, "X-Requested-With": "XMLHttpRequest" },
      });
      if (res.ok) show((await res.json()) as WaLineStatus);
    } finally {
      connect.disabled = false;
    }
  });

  show({
    status: root.dataset.status || "unreachable",
    linked: root.dataset.linked === "1",
    phone: root.dataset.phone || null,
    name: root.dataset.name || null,
    qr: null,
    lastError: root.dataset.error || null,
  });
  // The first render has no QR image (it is not put in the page); fetch it right away if one is up.
  if (root.dataset.status === "qr") void poll();
}

export function initWhatsApp(): void {
  initWaThread();
  document.querySelectorAll<HTMLElement>("#wa-line, [data-wa-line]").forEach(initWaLine);
}
